import React, { useEffect, useRef, useState } from "react";
import TypingTestPlayer from "./TypingTestPlayer";
import "./TypingTestDemo.scss";
import "../My-assessment/myAssessment.scss";

// Internal-only preview route (see /typing-test-demo in App.js) so the typing test UI can be
// reviewed before the backend submit-typing-result endpoint exists.
// The header/timer/nav chrome below mirrors candidateTest.js's "test" screen purely for visual
// reference — it is not wired to real test/session state. Uses a hardcoded passage + a local
// 5-minute timer, then renders a sample report card in the same style as
// My-assessment/test-report.js. Delete this file, TypingTestDemo.scss, and the /typing-test-demo
// route in App.js once the real integration is done.

const DEMO_PASSAGE =
  "The quick brown fox jumps over the lazy dog. Programming is the art of telling another " +
  "human what one wants the computer to do. Typing quickly and accurately is a skill built " +
  "through repetition, focus, and steady practice over time. Every keystroke counts toward " +
  "your overall speed and precision score, so stay relaxed and keep a steady rhythm. Good " +
  "typing posture starts with a straight back, relaxed shoulders, and wrists that hover just " +
  "above the keyboard rather than resting heavily on the desk. Many professional typists " +
  "recommend the touch typing method, where each finger is assigned to a specific set of keys " +
  "so the eyes never need to leave the screen. Consistent practice, even for just ten or " +
  "fifteen minutes a day, can noticeably improve both speed and accuracy within a few weeks. " +
  "Speed without accuracy is not particularly useful, since every mistake usually costs more " +
  "time to correct than it would have taken to type the character correctly in the first " +
  "place. That is why most typing assessments measure net words per minute rather than gross " +
  "words per minute, since net speed accounts for uncorrected errors and gives a more " +
  "realistic picture of real world typing performance. Accuracy percentage is calculated by " +
  "dividing the number of correctly typed characters by the total number of characters typed, " +
  "then multiplying the result by one hundred. A candidate who types very quickly but makes " +
  "frequent mistakes will often score lower overall than a candidate who types at a moderate " +
  "pace with very few errors. Employers in many industries value strong typing skills because " +
  "they translate directly into productivity gains across a wide range of daily tasks, from " +
  "writing emails and reports to entering data and chatting with colleagues and customers. " +
  "Modern keyboards, whether mechanical or membrane based, are designed to provide tactile " +
  "feedback that helps typists know when a key press has registered without needing to glance " +
  "down at their hands. Ergonomic keyboards go a step further by splitting the key layout to " +
  "reduce strain on the wrists during long typing sessions. Regardless of the equipment used, " +
  "the fundamentals of good typing remain the same: consistent finger placement, a comfortable " +
  "typing posture, and regular breaks to avoid fatigue. As this passage continues, notice how " +
  "your speed and accuracy may naturally fluctuate depending on sentence complexity, " +
  "punctuation density, and how tired your fingers are becoming. Longer passages like this one " +
  "are useful for typing assessments because they give a much more stable and representative " +
  "measurement of typing ability than a short sentence would, smoothing out the natural " +
  "variance between individual words and phrases. Keep typing at a pace that feels sustainable " +
  "rather than rushing, since a steady and consistent rhythm usually produces better long term " +
  "results than short bursts of very fast but error prone typing. If you finish before the " +
  "timer runs out, use the Submit button below the passage to end the attempt early instead of " +
  "waiting for time to expire. Thank you for taking the time to test this typing assessment " +
  "screen, and good luck reaching the end of the passage before the timer runs out.";

const DEMO_DURATION_SEC = 5 * 60;

function formatDuration(totalSeconds) {
  const safe = Math.max(0, Math.round(totalSeconds || 0));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
}

export default function TypingTestDemo() {
  const [finished, setFinished] = useState(false);
  const [report, setReport] = useState(null);
  const [displaySeconds, setDisplaySeconds] = useState(DEMO_DURATION_SEC);
  const [showNote, setShowNote] = useState(true);

  const timeLeftRef = useRef(DEMO_DURATION_SEC);
  const latestPayloadRef = useRef(null);
  const finishedRef = useRef(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (finishedRef.current) return;
      timeLeftRef.current = Math.max(0, timeLeftRef.current - 1);
      setDisplaySeconds(timeLeftRef.current);
      if (timeLeftRef.current === 0) {
        finishedRef.current = true;
        clearInterval(interval);
        setReport(latestPayloadRef.current);
        setFinished(true);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleProgress = (payload) => {
    latestPayloadRef.current = payload;
  };

  const handleSubmit = (payload) => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    setReport(payload);
    setFinished(true);
  };

  if (finished) {
    return <TypingTestSampleReport report={report} passage={DEMO_PASSAGE} />;
  }

  return (
    <div className="typing-demo-page">
      <div className="typing-demo-topbar">
        <span>Candidate Test Assessment</span>
        <span>Assessment: Typing-Test-Demo</span>
      </div>

      <div className="typing-demo-header">
        <div className="typing-demo-header-left">
          <h3 className="typing-demo-header-title">Typing_Test_Demo</h3>
          <button className="typing-demo-report-btn" disabled title="Disabled in preview">
            ⚠ Report Issue
          </button>
        </div>
        <div className="typing-demo-candidate">
          <img src="/images/user-icon-wight-fill.svg" alt="" width={20} height={20} />
          <span>Demo Candidate</span>
        </div>
      </div>

      <div className="typing-demo-timer">
        <span>{formatDuration(displaySeconds)}</span>
      </div>

      <div className="typing-demo-nav">
        <button className="typing-demo-nav-btn" disabled title="Disabled in preview">Prev</button>
        <button className="typing-demo-nav-btn" disabled title="Disabled in preview">Next</button>
      </div>

      {showNote && (
        <div className="typing-demo-note">
          <span className="typing-demo-note-icon">⚠️</span>
          <span className="typing-demo-note-text">
            <strong>Note:</strong> This is an internal preview screen — the submit-typing-result
            backend endpoint isn't live yet, so this demo uses a hardcoded passage and a local
            5-minute timer instead of a real test session, and the report below is a preview only.
          </span>
          <button className="typing-demo-note-close" onClick={() => setShowNote(false)} title="Dismiss">
            ✕
          </button>
        </div>
      )}

      <div className="typing-demo-content">
        <TypingTestPlayer
          question={{ question: DEMO_PASSAGE }}
          onProgress={handleProgress}
          onSubmit={handleSubmit}
        />
      </div>
    </div>
  );
}

function TypingTestSampleReport({ report, passage }) {
  const grossWPM = report?.grossWPM ?? 0;
  const netWPM = report?.netWPM ?? 0;
  const accuracyPct = report?.accuracyPct ?? 0;
  const errorCount = report?.errorCount ?? 0;
  const backspaceRatePct = (report?.backspaceRate ?? 0) * 100;

  const cards = [
    {
      variant: "blue",
      label: "Gross WPM",
      value: grossWPM.toFixed(1),
      fillPct: Math.min(100, (grossWPM / 100) * 100),
    },
    {
      variant: "green",
      label: "Net WPM",
      value: netWPM.toFixed(1),
      fillPct: Math.min(100, (netWPM / 100) * 100),
    },
    {
      variant: "purple",
      label: "Accuracy",
      value: `${accuracyPct.toFixed(1)}%`,
      fillPct: Math.min(100, accuracyPct),
    },
    {
      variant: "gold",
      label: "Error count",
      value: errorCount,
      fillPct: Math.min(100, (report?.totalCharsTyped ? (errorCount / report.totalCharsTyped) * 100 : 0)),
    },
    {
      variant: "neutral",
      label: "Backspace rate",
      value: `${backspaceRatePct.toFixed(1)}%`,
      fillPct: Math.min(100, backspaceRatePct),
    },
  ];

  return (
    <div className="typing-demo-page">
      <div className="typing-demo-banner">
        Sample report preview — Typing Test results will surface as a metrics card  → Test Report.
      </div>

      <div className="typing-demo-content">
        <div className="test-report-holder">
          <div className="assessment-summary-v2" style={{ borderBottom: "1px solid #E4E9F0", padding: 20 }}>
            <h4 style={{ marginBottom: 12 }}>Typing Test — Result Summary</h4>
            <div className="summary-cards-row">
              {cards.map((card) => (
                <div key={card.label} className={`summary-score-card summary-score-card--${card.variant}`}>
                  <div className={`summary-card-icon-wrap icon-${card.variant === "neutral" ? "gold" : card.variant}`}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="9" stroke="#374151" strokeWidth="2" />
                      <path d="M12 7v5l3 3" stroke="#374151" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </div>
                  <h2 className="summary-card-num">{card.value}</h2>
                  <p className="summary-card-label">{card.label}</p>
                  <div className="summary-card-track">
                    <div className={`summary-card-fill fill-${card.variant}`} style={{ width: `${card.fillPct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="individual-report" style={{ padding: "0 20px 20px" }}>
            <h4>Attempt Details</h4>
            <p>Time taken: {formatDuration(report?.timeTakenSec)}</p>
            <p>Keystrokes (all attempts): {report?.totalCharsTyped ?? 0}</p>
            <p>Passage delivered: {report?.finalCharsTyped ?? report?.totalCharsTyped ?? 0} / {passage.length}</p>
            <p>Correct characters: {report?.correctChars ?? 0}</p>
            <p>Incorrect characters (incl. corrected): {report?.incorrectChars ?? 0}</p>
            <p>Backspaces: {report?.backspaceCount ?? 0}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
